<div class="content-info">
    <div class="content-title d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h3 class="title mb-0">Roles &amp; Access Control</h3>
        <div class="d-flex align-items-center gap-2 flex-wrap">
            <div class="input-group" style="max-width: 280px;">
                <input type="text" class="form-control" placeholder="New role name"
                    [(ngModel)]="newRoleName" (keyup.enter)="createRole()" />
                <button class="btn btn-outline-primary text-nowrap" (click)="createRole()"
                    [disabled]="creatingRole || !newRoleName.trim()">
                    <i class="bi bi-plus-lg me-1"></i> Add Role
                </button>
            </div>
            <button class="btn btnprimary text-white text-nowrap" (click)="save()"
                [disabled]="saving || !dirty || !selectedRoleId">
                @if (saving) {
                <span class="spinner-border spinner-border-sm me-1"></span>
                }
                Save Changes
            </button>
        </div>
    </div>

    <div class="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0 py-2">
        <i class="bi bi-shield-check"></i>
        <div class="small">
            <strong>{{ superUserNames }}</strong> always have full access to every page and cannot be
            restricted. The <strong>Warehouse / Store</strong> switches control where a page is available at
            all — they apply to every role, on top of the per-role access level.
        </div>
    </div>

    <div class="d-flex align-items-center gap-3 mt-3 flex-wrap">
        <div class="d-flex align-items-center gap-2">
            <label class="fw-semibold mb-0 text-nowrap">Configure role:</label>
            <select class="form-select" style="min-width: 220px;" [(ngModel)]="selectedRoleId"
                (ngModelChange)="onRoleChange()">
                @for (role of editableRoles; track role.id) {
                <option [ngValue]="role.id">{{ role.name }}</option>
                }
            </select>
        </div>
        <div class="small text-muted">
            <span class="badge bg-secondary me-1">None</span> hidden
            <span class="badge bg-info text-dark mx-1">View</span> read-only
            <span class="badge bg-success ms-1">Full</span> view + edit
        </div>
        @if (dirty) {
        <span class="badge bg-warning text-dark"><i class="bi bi-exclamation-triangle me-1"></i>Unsaved changes</span>
        }
    </div>

    <div class="common-table mt-4">
        <div class="table-responsive">
            <table class="table align-middle">
                <thead>
                    <tr>
                        <th style="min-width: 260px;">Page / Tab</th>
                        <th style="min-width: 220px;">Access for {{ selectedRole?.name || 'role' }}</th>
                        <th class="text-center" style="min-width: 200px;">Available at</th>
                    </tr>
                </thead>
                <tbody>
                    @if (loading) {
                    <tr>
                        <td colspan="3" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </td>
                    </tr>
                    } @else {
                    @for (group of moduleGroups; track group.module) {
                    <tr class="table-light">
                        <td class="fw-bold">
                            <i class="bi bi-folder2-open me-2"></i>{{ group.module }}
                        </td>
                        <td>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" (click)="setAllInModule(group, 'NONE')"
                                    title="Set all pages in this module to None">All None</button>
                                <button class="btn btn-outline-info" (click)="setAllInModule(group, 'VIEW')"
                                    title="Set all pages in this module to View">All View</button>
                                <button class="btn btn-outline-success" (click)="setAllInModule(group, 'FULL')"
                                    title="Set all pages in this module to Full">All Full</button>
                            </div>
                        </td>
                        <td></td>
                    </tr>
                    @for (page of group.pages; track page.key) {
                    <tr>
                        <td>
                            <div [class.ps-4]="page.parentKey">
                                @if (page.parentKey) {
                                <i class="bi bi-arrow-return-right me-1 text-muted"></i>
                                }
                                <span class="fw-semibold">{{ page.name }}</span>
                                @if (page.parentKey) {
                                <span class="badge bg-light text-dark border ms-2">tab</span>
                                }
                            </div>
                        </td>
                        <td>
                            <div class="btn-group btn-group-sm" role="group">
                                @for (level of accessLevels; track level) {
                                <button type="button" class="btn" [ngClass]="{
                                        'btn-secondary': level === 'NONE' && getAccess(page.key) === 'NONE',
                                        'btn-info': level === 'VIEW' && getAccess(page.key) === 'VIEW',
                                        'btn-success': level === 'FULL' && getAccess(page.key) === 'FULL',
                                        'btn-outline-secondary': getAccess(page.key) !== level
                                    }" (click)="setAccess(page.key, level)">
                                    {{ level === 'NONE' ? 'None' : level === 'VIEW' ? 'View' : 'Full' }}
                                </button>
                                }
                            </div>
                        </td>
                        <td class="text-center">
                            <div class="d-inline-flex align-items-center gap-3"
                                [class.opacity-50]="savingLocation.has(page.key)">
                                <div class="form-check form-switch m-0 d-inline-flex align-items-center">
                                    <input class="form-check-input" type="checkbox"
                                        [checked]="isLocationAllowed(page, 'Warehouse')"
                                        [disabled]="savingLocation.has(page.key)"
                                        (change)="toggleLocationType(page, 'Warehouse')" />
                                    <span class="ms-1 small">Warehouse</span>
                                </div>
                                <div class="form-check form-switch m-0 d-inline-flex align-items-center">
                                    <input class="form-check-input" type="checkbox"
                                        [checked]="isLocationAllowed(page, 'Store')"
                                        [disabled]="savingLocation.has(page.key)"
                                        (change)="toggleLocationType(page, 'Store')" />
                                    <span class="ms-1 small">Store</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                    }
                    } @empty {
                    <tr>
                        <td colspan="3" class="text-center py-5 text-muted">No pages found.</td>
                    </tr>
                    }
                    }
                </tbody>
            </table>
        </div>
    </div>
</div>
